import { createMomentStore, normalizeMoment } from '@theme/loumi-moments-core';

const ROOT_SELECTOR = '[data-loumi-moment-root]';
const BUTTON_SELECTOR = '[data-loumi-moment-button]';
const PANEL_SELECTOR = '[data-loumi-panel]';
const controllers = new WeakMap();

// CSS transitions retain their interpolated pose when a selection is interrupted.
// The floating child is separate so its animation never overrides that pose.
function updateSpatial(root, moment) {
  const items = [...root.querySelectorAll('[data-loumi-spatial-item]')];
  const activeIndex = items.findIndex((item) => item.dataset.loumiSpatialItem === moment);
  for (const [index, item] of items.entries()) {
    const distance = (index - activeIndex + items.length) % items.length;
    item.dataset.position = distance === 0 ? 'active' : distance === 1 ? 'next' : distance === items.length - 1 ? 'previous' : 'away';
  }
}

function availableStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function rootsWithin(scope = document) {
  const roots = [];
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
  roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
  return roots;
}

function getDefaultMoment() {
  const root = document.querySelector(ROOT_SELECTOR);
  return normalizeMoment(root?.dataset.defaultMoment, 'paz');
}

const store = createMomentStore({
  storage: availableStorage(),
  defaultMoment: getDefaultMoment(),
});

function applyMoment(moment, { announce = true } = {}) {
  const nextMoment = store.set(moment);

  for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
    root.dataset.currentMoment = nextMoment;
    root.style.setProperty('--loumi-active-color', `var(--loumi-${nextMoment})`);
    if (root.hasAttribute('data-loumi-spatial')) updateSpatial(root, nextMoment);

    for (const button of root.querySelectorAll(BUTTON_SELECTOR)) {
      button.setAttribute('aria-pressed', String(button.dataset.loumiMomentButton === nextMoment));
    }

    const displaysAll = root.dataset.loumiDisplay === 'all';
    for (const panel of root.querySelectorAll(PANEL_SELECTOR)) {
      const isActive = panel.dataset.loumiPanel === nextMoment;
      panel.dataset.active = String(isActive);
      panel.hidden = !displaysAll && !isActive;
    }
  }

  if (announce) {
    document.dispatchEvent(new CustomEvent('loumi:moment-change', { detail: { moment: nextMoment } }));
  }
  return nextMoment;
}

function initializeRoot(root) {
  if (root.dataset.loumiInitialized === 'true') return;

  const abortController = new AbortController();
  let suppressClickUntil = 0;
  root.addEventListener('click', (event) => {
    if (performance.now() < suppressClickUntil) {
      event.preventDefault();
      return;
    }
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button || !root.contains(button)) return;
    applyMoment(button.dataset.loumiMomentButton);
  }, { signal: abortController.signal });

  if (root.hasAttribute('data-loumi-spatial')) {
    const signal = abortController.signal;
    const buttons = [...root.querySelectorAll(BUTTON_SELECTOR)];
    const selectAdjacent = (direction) => {
      if (!buttons.length) return;
      const index = buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
      const button = buttons[(index + direction + buttons.length) % buttons.length];
      applyMoment(button.dataset.loumiMomentButton);
      return button;
    };
    root.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) return;
      const control = event.target.closest('[data-loumi-direction]');
      if (control && root.contains(control)) selectAdjacent(Number(control.dataset.loumiDirection));
    }, { signal });
    root.addEventListener('keydown', (event) => {
      if (!event.target.matches(BUTTON_SELECTOR) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key.startsWith('Arrow')) selectAdjacent(event.key === 'ArrowRight' ? 1 : -1).focus();
      else {
        const button = event.key === 'Home' ? buttons[0] : buttons.at(-1);
        applyMoment(button.dataset.loumiMomentButton);
        button.focus();
      }
    }, { signal });
    const stage = root.querySelector('[data-loumi-swipe]');
    const surface = root.querySelector('[data-loumi-swipe-surface]') || stage;
    let pointer;
    surface.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) { pointer = undefined; return; }
      suppressClickUntil = 0;
      if (event.target.closest('[data-loumi-direction], a')) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
    }, { signal });
    surface.addEventListener('pointermove', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
        // Decide once: a vertical scroll must never turn into a carousel swipe.
        pointer.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
        if (pointer.axis === 'x') surface.setPointerCapture(event.pointerId);
      }
    }, { signal, passive: true });
    surface.addEventListener('pointerup', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      const horizontal = pointer.axis !== 'y' && Math.abs(dx) > Math.abs(dy) * 1.2;
      pointer = undefined;
      if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
      if (horizontal && Math.abs(dx) >= 36) {
        suppressClickUntil = performance.now() + 400;
        selectAdjacent(dx < 0 ? 1 : -1);
      }
    }, { signal });
    surface.addEventListener('pointercancel', () => { pointer = undefined; }, { signal });
    surface.addEventListener('lostpointercapture', (event) => {
      if (event.target === surface) pointer = undefined;
    }, { signal });
    const observer = new IntersectionObserver(([entry]) => {
      root.dataset.spatialVisible = String(entry.isIntersecting && !document.hidden);
    }, { threshold: 0.08 });
    observer.observe(stage);
    document.addEventListener('visibilitychange', () => {
      const bounds = stage.getBoundingClientRect();
      root.dataset.spatialVisible = String(!document.hidden && bounds.bottom > 0 && bounds.top < window.innerHeight);
    }, { signal });
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  root.dataset.loumiInitialized = 'true';
  controllers.set(root, abortController);
}

function initialize(scope = document) {
  for (const root of rootsWithin(scope)) initializeRoot(root);
  applyMoment(store.get(), { announce: false });
  document.documentElement.classList.add('loumi-enhanced');
}

function start() {
  initialize(document);

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const root of rootsWithin(event.target)) {
      controllers.get(root)?.abort();
      controllers.delete(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

export { applyMoment, initialize };

